import { apiClient } from '../utils/api-client';
import type {
  HoldType,
  HoldTypeInput,
  HoldTypeList,
  HoldTypeListOptions,
} from '../types/hold-type';

interface HoldTypeRow {
  id: string;
  type: string;
  attributes: HoldType;
}

/**
 * A place's hold types. One list, two audiences: the picker in the climb form
 * reads it, and the route-setting panel edits it.
 *
 * The path segment says `gyms` for a competition too — the same trade the rest
 * of the panel's endpoints make (see the LocatesLocation concern on the server).
 * The id may be a slug or a numeric id, which is what lets the climb editor pass
 * whatever it already has.
 */
class HoldTypesService {
  private path(locationId: string | number): string {
    return `/api/gyms/${locationId}/hold_types`;
  }

  /**
   * One page of the list. `query` is matched server-side, so the search box
   * reaches the whole list rather than only what is on screen.
   */
  async list(
    locationId: string | number,
    options: HoldTypeListOptions = {}
  ): Promise<HoldTypeList> {
    const params = new URLSearchParams();
    if (options.query?.trim()) params.append('q', options.query.trim());
    if (options.page) params.append('page', String(options.page));
    if (options.perPage) params.append('per_page', String(options.perPage));
    const suffix = params.toString() ? `?${params.toString()}` : '';

    const response = await apiClient.get(`${this.path(locationId)}${suffix}`);
    if (!response.ok) throw new Error('Failed to fetch hold types');

    const body = await response.json();
    return {
      types: (body.data as HoldTypeRow[]).map((row) => row.attributes),
      canManage: Boolean(body.meta?.can_manage),
      page: body.meta?.page ?? 1,
      perPage: body.meta?.per_page ?? 25,
      total: body.meta?.total ?? 0,
      hasMore: Boolean(body.meta?.has_more),
    };
  }

  async create(locationId: string | number, input: HoldTypeInput): Promise<HoldType> {
    const response = await apiClient.post(this.path(locationId), {
      data: { attributes: input },
    });
    if (!response.ok) throw new Error(await errorMessage(response, 'Failed to create hold type'));

    const body = await response.json();
    return (body.data as HoldTypeRow).attributes;
  }

  async update(
    locationId: string | number,
    id: string,
    input: HoldTypeInput
  ): Promise<HoldType> {
    const response = await apiClient.patch(`${this.path(locationId)}/${id}`, {
      data: { attributes: input },
    });
    if (!response.ok) throw new Error(await errorMessage(response, 'Failed to update hold type'));

    const body = await response.json();
    return (body.data as HoldTypeRow).attributes;
  }

  /**
   * Retire a type. `reassignTo` moves its climbs onto another of the place's
   * types; without it they are left without one.
   */
  async remove(
    locationId: string | number,
    id: string,
    reassignTo: string | null = null
  ): Promise<void> {
    const query = reassignTo ? `?reassign_to=${encodeURIComponent(reassignTo)}` : '';
    const response = await apiClient.delete(`${this.path(locationId)}/${id}${query}`);
    if (!response.ok) throw new Error(await errorMessage(response, 'Failed to delete hold type'));
  }
}

/** The server's own wording where it gave one — a name already taken, say. */
async function errorMessage(response: Response, fallback: string): Promise<string> {
  try {
    const body = await response.json();
    return body?.errors?.[0]?.detail || fallback;
  } catch {
    return fallback;
  }
}

export const holdTypesService = new HoldTypesService();
